import * as THREE from "three";

export const BLOCK = {
    Empty: 0,
    Bedrock: 1,
    Stone: 2,
    Dirt: 3,
    Grass: 4,
    Sand: 5,
    Sandstone: 6,
    Water: 7,
    Ice: 8,
    SnowBlock: 9,
    Log: 10,
    Leaves: 11,
    PineLeaves: 12,
    Cactus: 13,
    FlowerRed: 14,
    FlowerYellow: 15,
    Mushroom: 16,
    LilyPad: 17,
    IceSpike: 18,
    Rock: 19,
    TallGrass: 20,
    DeadBush: 21,
    CoarseDirt: 22,
    Terracotta: 23,
    RedTerracotta: 24,
    WhiteTerracotta: 25,
    Mycelium: 26,
    MushroomStem: 27,
    MushroomCapRed: 28,
    MushroomCapBrown: 29,
    Netherrack: 30,
    Lava: 31,
    Glowstone: 32,
    Obsidian: 33,
    Portal: 34,
    Planks: 35,
    DarkPlanks: 36,
    Thatch: 37,
    MossBlock: 38,
    StructureLog: 39,
    StructureSnow: 40,
} as const;

export type BlockId = (typeof BLOCK)[keyof typeof BLOCK];
export type BlockKind = Exclude<BlockId, typeof BLOCK.Empty>;
export type FluidBlock = typeof BLOCK.Water | typeof BLOCK.Ice | typeof BLOCK.Lava;

export class VoxelWorld {
    readonly data: Uint8Array;

    constructor(
        readonly size: number,
        readonly height: number,
        data?: Uint8Array,
    ) {
        const length = size * size * height;
        if (data && data.length !== length) {
            throw new RangeError(`Expected ${length} voxels, received ${data.length}`);
        }
        this.data = data ?? new Uint8Array(length);
    }

    columnOffset(x: number, z: number): number {
        return (x * this.size + z) * this.height;
    }

    get(x: number, y: number, z: number): BlockId {
        if (
            x < 0 ||
            y < 0 ||
            z < 0 ||
            x >= this.size ||
            y >= this.height ||
            z >= this.size
        ) return BLOCK.Empty;
        return this.data[(x * this.size + z) * this.height + y] as BlockId;
    }

    set(x: number, y: number, z: number, block: BlockId): void {
        if (
            x < 0 ||
            y < 0 ||
            z < 0 ||
            x >= this.size ||
            y >= this.height ||
            z >= this.size
        ) return;
        this.data[(x * this.size + z) * this.height + y] = block;
    }

    fillColumn(x: number, z: number, fromY: number, toY: number, block: BlockId): void {
        if (x < 0 || z < 0 || x >= this.size || z >= this.size) return;
        const from = Math.max(0, fromY);
        const to = Math.min(this.height - 1, toY);
        if (from > to) return;
        const offset = this.columnOffset(x, z);
        this.data.fill(block, offset + from, offset + to + 1);
    }

    clone(): VoxelWorld {
        return new VoxelWorld(this.size, this.height, this.data.slice());
    }
}

export const BIOME = {
    Icy: "icy",
    ColdForest: "cold_forest",
    Temperate: "temperate",
    Desert: "desert",
    Humid: "humid",
    Savanna: "savanna",
    Swamp: "swamp",
    Taiga: "taiga",
    Mesa: "mesa",
    Mushroom: "mushroom",
} as const;

export type Biome = (typeof BIOME)[keyof typeof BIOME];

export const TREE = {
    Oak: "oak",
    Pine: "pine",
    MushroomRed: "mushroom_red",
    MushroomBrown: "mushroom_brown",
} as const;

export type TreeType = (typeof TREE)[keyof typeof TREE];

export interface BiomeMap {
    temp: number[][]; // 0..1
    humid: number[][]; // 0..1
    biome: Biome[][]; // primary classification per cell
}

export interface SceneParts {
    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    camera: THREE.Camera;
    group: THREE.Group;
}
